import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PermissionContext = createContext(null);

function match(grant, needed) {
  if (grant === needed) return true;
  if (grant.includes("*")) {
    const pattern = new RegExp("^" + grant.split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*") + "$");
    return pattern.test(needed);
  }
  return false;
}

export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get("/permissions/me");
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const grants = data?.permissions || [];
    const has = (key) => grants.some((g) => match(g, key));
    const hasAny = (keys) => keys.some(has);
    const hasAll = (keys) => keys.every(has);
    return {
      loading,
      role: data?.role,
      roleName: data?.role_name,
      isExecutive: !!data?.is_executive,
      permissions: grants,
      has, hasAny, hasAll,
      refresh,
    };
  }, [data, loading, refresh]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() { return useContext(PermissionContext) || { has: () => false, hasAny: () => false, hasAll: () => false, permissions: [], loading: true }; }
